import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ContentApi } from '../../../../shared/api/contentApi';
import PaginationFooter from '../../../../components/ui/PaginationFooter';
import ParameterCreateDialog from '../components/ParameterCreateDialog';
import { useContentContext } from '../../ContentContext';
import { resolvePagedTotals } from '../../../../shared/helpers/pagination';
import formatDate from '../../../../shared/helpers/formatDate';
import UuidCell from '../../../../components/ui/UuidCell';
import TableCellPreview from '../../../../components/ui/TableCellPreview';
import { useListTableState } from '../../../../shared/hooks/useListTableState';

import { BaseTable } from '../../../../components/ui/ant/Table';
import { Flex, Input, Select } from 'antd';

const GLOBAL_GROUP_PAGE_SIZE = 200;

export default function ListParameterPage() {
    const navigate = useNavigate();
    const { canMutate, selectedVersion, projectId, versionId } = useContentContext();
    const {
        qInput,
        setQInput,
        qDebounced,
        sortBy,
        setSortBy,
        sortDir,
        setSortDir,
        groupBy,
        setGroupBy,
        groupScope,
        setGroupScope,
        page,
        setPage,
        pageSize,
        setPageSize,
        total,
        setTotal,
        totalPages,
        setTotalPages,
        safePage,
    } = useListTableState({
        initialSortBy: 'name',
        initialSortDir: 'asc',
        initialGroupBy: 'none',
        initialPageSize: 6,
        debounceMs: 1450,
        enableGroupScope: true,
    });

    const [parameters, setParameters] = useState([]);
    const [globalParameters, setGlobalParameters] = useState([]);

    const [loading, setLoading] = useState(false);
    const [globalLoading, setGlobalLoading] = useState(false);
    const [error, setError] = useState('');
    const [globalError, setGlobalError] = useState('');

    const [createOpen, setCreateOpen] = useState(false);

    useEffect(() => {
        if (!projectId || !versionId) {
            setParameters([]);
            setTotal(0);
            setTotalPages(1);
            setLoading(false);
            return;
        }

        const ac = new AbortController();
        setLoading(true);
        setError('');

        ContentApi.getParametersPaged({ page, pageSize, search: qDebounced || undefined }, { signal: ac.signal })
            .then((data) => {
                setParameters(Array.isArray(data?.items) ? data.items : []);
                const paging = resolvePagedTotals(data, pageSize);
                setTotal(paging.total);
                setTotalPages(paging.totalPages);
            })
            .catch((e) => !ac.signal.aborted && setError(e.message || 'Ошибка загрузки'))
            .finally(() => !ac.signal.aborted && setLoading(false));

        return () => ac.abort();
    }, [page, pageSize, projectId, qDebounced, versionId]);

    useEffect(() => {
        if (!projectId || !versionId || groupBy === 'none' || groupScope !== 'global') {
            setGlobalParameters([]);
            setGlobalError('');
            setGlobalLoading(false);
            return;
        }

        const ac = new AbortController();

        const fetchAllPages = async () => {
            setGlobalLoading(true);
            setGlobalError('');

            try {
                const firstPage = await ContentApi.getParametersPaged(
                    { page: 1, pageSize: GLOBAL_GROUP_PAGE_SIZE, search: qDebounced || undefined },
                    { signal: ac.signal },
                );

                if (ac.signal.aborted) return;

                const firstItems = Array.isArray(firstPage?.items) ? firstPage.items : [];
                const paging = resolvePagedTotals(firstPage, GLOBAL_GROUP_PAGE_SIZE);
                const pageNumbers = Array.from(
                    { length: Math.max(0, paging.totalPages - 1) },
                    (_, index) => index + 2,
                );

                const aggregated = [...firstItems];
                const chunkSize = 5;

                for (let index = 0; index < pageNumbers.length; index += chunkSize) {
                    const chunk = pageNumbers.slice(index, index + chunkSize);
                    const responses = await Promise.all(
                        chunk.map((currentPage) =>
                            ContentApi.getParametersPaged(
                                { page: currentPage, pageSize: GLOBAL_GROUP_PAGE_SIZE, search: qDebounced || undefined },
                                { signal: ac.signal },
                            ),
                        ),
                    );

                    if (ac.signal.aborted) return;

                    responses.forEach((response) => {
                        const items = Array.isArray(response?.items) ? response.items : [];
                        aggregated.push(...items);
                    });
                }

                setGlobalParameters(aggregated);
            } catch (e) {
                if (!ac.signal.aborted) {
                    setGlobalParameters([]);
                    setGlobalError(e?.message || 'Ошибка агрегированной загрузки');
                }
            } finally {
                if (!ac.signal.aborted) {
                    setGlobalLoading(false);
                }
            }
        };

        fetchAllPages();

        return () => ac.abort();
    }, [groupBy, groupScope, projectId, qDebounced, versionId]);

    const activeRows = useMemo(() => {
        if (groupBy !== 'none' && groupScope === 'global') {
            return globalParameters;
        }
        return parameters;
    }, [globalParameters, groupBy, groupScope, parameters]);

    const groupedWithGlobal = groupBy !== 'none' && groupScope === 'global';
    const displayLoading = loading || globalLoading;
    const displayError = error || globalError;

    return (
        <section className="p-4 md:p-6 space-y-4">
            <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="space-y-1">
                    <h1 className="text-xl font-semibold">
                        Параметры
                    </h1>
                    <p className="text-sm text-zinc-200">
                        Данные с SbContentAPI
                    </p>
                </div>
                <button
                    type="button"
                    className="btn btn-soft-accent"
                    onClick={() => navigate(`/content/parameters/list`)}
                >
                    Карточки
                </button>
                <button
                    type="button"
                    className="btn btn-soft-accent"
                    onClick={() => setCreateOpen(true)}
                    disabled={!canMutate}
                >
                    Создать параметр
                </button>
                <Flex flex wrap="wrap" gap="medium" align="end">
                    <Flex flex vertical>
                        <label className="eyebrow mb-1" htmlFor="parameter-search">Поиск</label>
                        <Input
                            id="parameter-search"
                            value={qInput}
                            onChange={(e) => setQInput(e.target.value)}
                            placeholder="Имя или systemName…"
                            className="w-64"
                        />
                    </Flex>

                    {groupBy !== 'none' && (
                        <Flex flex vertical>
                            <label className="eyebrow mb-1">Режим группировки</label>
                            <Select
                                value={groupScope}
                                onChange={(value) => setGroupScope(value)}
                            >
                                <Select.Option value="page">Текущая страница</Select.Option>
                                <Select.Option value="global">Все данные (агрегация)</Select.Option>
                            </Select>
                        </Flex>
                    )}
                </Flex>
            </header>

            {displayError && (
                <div className="rounded-xl border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
                    {displayError}
                </div>
            )}

            {!canMutate && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-700 p-3 text-sm">
                    Текущая версия ({selectedVersion?.status || "unknown"}) только для чтения. Используйте Fork в draft для изменений.
                </div>
            )}

            {groupedWithGlobal && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 text-blue-700 p-3 text-sm">
                    Глобальная группировка выполняется по всем данным версии. Это может занять некоторое время.
                </div>
            )}

            {displayLoading ? (
                <div className="p-6 text-sm text-zinc-500">Загрузка…</div>
            ) : (
                <>
                    <BaseTable
                        columns={[
                            {
                                key: 'id',
                                header: 'ID',
                                render: (value) => <UuidCell value={value} />,
                            },
                            { key: "name", header: "Имя", sortable: true },
                            { key: "systemName", header: "Системное имя", sortable: true },
                            { 
                                key: "valueTypeCode", 
                                header: "Тип значения",
                                groupable: true,
                                render: (value) => <TableCellPreview value={value} maxLength={32} mono />,
                            },                            
                            {
                                key: 'defaultValueJson',
                                header: 'Значение по умолчанию',
                                render: (value) => <TableCellPreview value={value} maxLength={32} mono />,
                            },
                            {
                                key: 'parameterGroupId',
                                header: 'Группа параметров',
                                groupable: true,
                                render: (value) => <UuidCell value={value} />,
                            },
                            { key: "isRequired", header: "Обязательный", groupable: true },
                            {
                                key: 'updatedAt',
                                header: 'Обновлено',
                                sortable: true,
                                render: (value) => <span title={value || '-'}>{formatDate(value)}</span>,
                            },
                        ]}
                        className="w-full h-full"
                        data={activeRows}
                        searchQuery={qInput}
                        searchKeys={['name', 'systemName', 'description', 'valueTypeCode']}
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSortChange={(key, dir) => {
                            setSortBy(key);
                            if (dir) setSortDir(dir);
                        }}
                        groupBy={groupBy === 'none' ? undefined : groupBy}
                        onGroupByChange={(key) => setGroupBy(key ?? 'none')}
                        groupByResolver={(row, field) => {
                            if (field === 'isRequired') {
                                return row?.isRequired ? 'Обязательные' : 'Необязательные';
                            }
                            return row?.[field];
                        }}
                        pagination={{
                            current: safePage,
                            pageSize,
                            total,
                        }}
                        onChange={(pagination) => {
                            if (pagination?.current) {
                                setPage(pagination.current);
                            }
                            if (pagination?.pageSize && pagination.pageSize !== pageSize) {
                                setPageSize(pagination.pageSize);
                            }
                        }}
                        onRowClick={(row) => navigate(`/content/parameters/${row.id || row.parameterId}`)}
                    />
                </>
            )}

            <ParameterCreateDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={() => {
                    setCreateOpen(false);
                    setPage(1);
                }}
                projectId={projectId}
                versionId={versionId}
            />
        </section>
    );
}
